import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  inject,
  Input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { markEventAsHandled } from '@blaze-runtime/engine-util';
import { ContentEditableValueAccessorDirective, TooltipDirective } from '@blaze-shared/shared-ui';
import { moveCursorToEnd, stripHtmlTags, trackByField } from '@blaze-shared/shared-util';
import { FileResult, getItemTypeIcon } from '@blaze-shared/uploader';

import { ChatStore } from '../chat/chat.store';
import { ChatMessage } from '../chat.models';

const BASE_MAX_HEIGHT_PX = 95;
const GAP_BETWEEN_INPUT_AND_ATTACHMENTS_PX = 8;
const ATTACHMENTS_HEIGHT_PX = 64;

@UntilDestroy()
@Component({
  selector: 'be-chat-text-input',
  templateUrl: './chat-text-input.component.html',
  styleUrls: ['./chat-text-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => ChatTextInputComponent),
    },
  ],
  imports: [
    ReactiveFormsModule,
    ContentEditableValueAccessorDirective,
    MatIconModule,
    TooltipDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatTextInputComponent implements AfterViewInit {
  private readonly chatEditMessageService = inject(ChatStore);

  addAttachments = output<File[]>();
  deleteAttachment = output<FileResult>();
  sendMessage = output<string>();
  updateMessage = output<ChatMessage>();

  @Input() attachments: FileResult[] = [];
  @Input() chatId: string | undefined;
  @Input() inputDisabled = false;
  @Input() isViewAsMode = false;
  @Input() viewAsLabel: string | undefined;
  @Input() loading = false;
  @Input() placeholder = 'Chat';
  @Input() sendDisabled = false;

  @ViewChild('textInput') textInputElement: ElementRef<HTMLDivElement>;

  protected readonly markEventAsHandled = markEventAsHandled;

  isTextInputEmpty = signal(true);
  isEditMode = signal(false);
  editedMessage = signal<ChatMessage | undefined>(undefined);
  formControl = new FormControl('<p><br></p>');
  trackByUrl = trackByField<FileResult>('url');

  @HostBinding('style.max-height.px') get maxHeightPx(): number {
    return this.attachments.length > 0
      ? BASE_MAX_HEIGHT_PX + GAP_BETWEEN_INPUT_AND_ATTACHMENTS_PX + ATTACHMENTS_HEIGHT_PX
      : BASE_MAX_HEIGHT_PX;
  }

  get attachmentsImages(): Record<string, string> {
    return Object.fromEntries(
      this.attachments.map(({ filename }) => [filename, getItemTypeIcon(filename)]),
    );
  }

  constructor() {
    this.formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      const isTextInputEmpty = !value || stripHtmlTags(value).length === 0;

      if (isTextInputEmpty !== this.isTextInputEmpty()) {
        this.isTextInputEmpty.set(isTextInputEmpty);
      }
    });
  }

  ngAfterViewInit(): void {
    this.chatEditMessageService
      .getEditMessage(this.chatId)
      .pipe(untilDestroyed(this))
      .subscribe((message) => {
        if (!message) {
          this.isEditMode.set(false);
          this.editedMessage.set(undefined);
          return;
        }
        this.isEditMode.set(true);
        this.editedMessage.set(message);
        this.formControl.setValue(message?.content ? message.content : '');
        if (this.formControl.value && this.textInputElement?.nativeElement) {
          moveCursorToEnd(this.textInputElement.nativeElement);
        }
      });
  }

  uploadAttachments(event: Event): void {
    const fileInputElement = event.target as HTMLInputElement;
    const files = Array.from(fileInputElement?.files ?? []);
    fileInputElement.value = '';

    if (!files || files.length === 0) {
      return;
    }
    this.addAttachments.emit(files);
  }

  paste(event: ClipboardEvent): void {
    const items = event.clipboardData?.items;
    let isImagePasted = false;

    if (items) {
      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image')) {
          const file = item.getAsFile();

          if (file) {
            files.push(file);
            isImagePasted = true;
          }
        }
      }

      if (files.length) {
        this.addAttachments.emit(files);
      }
    }

    if (isImagePasted) {
      event.preventDefault();
      return;
    }
  }

  onEnterPress(event: Event): void {
    event.preventDefault();

    if (this.sendDisabled || (this.isTextInputEmpty() && this.attachments.length === 0)) {
      return;
    }

    this.submitMessage();
  }

  onEscapePress(): void {
    const chatId = this.chatId;
    if (!chatId) {
      return;
    }
    this.chatEditMessageService.resetEditMessage(chatId);
    this.attachments = [];
    this.resetTextInput(true);
    this.isEditMode.set(false);
  }

  submitMessage(): void {
    const message = stripHtmlTags(this.formControl.value ?? '');
    if (message.length > 0 || this.attachments.length > 0) {
      const editedMessage = this.editedMessage();
      if (this.isEditMode() && editedMessage) {
        editedMessage.content = message;
        this.updateMessage.emit(editedMessage);
        this.isEditMode.set(false);
      } else {
        this.sendMessage.emit(message);
      }
      this.attachments = [];
      this.resetTextInput(true);
    }
  }

  resetTextInput(emitEvent = false): void {
    this.formControl.setValue('', {
      emitEvent,
    });
  }
}
